import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  ArrowRight, 
  MapPin, 
  Clock, 
  Briefcase,
  Users,
  Globe,
  Award,
  ChevronDown,
  ChevronUp,
  Building2,
  GraduationCap,
  Heart,
  Loader2,
  CheckCircle2,
  Upload,
  Linkedin,
  Phone,
  Mail,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const benefits = [
  {
    icon: Globe,
    title: 'Global Impact',
    description: 'Work on projects that transform health systems across Africa and beyond.'
  },
  {
    icon: GraduationCap,
    title: 'Continuous Learning',
    description: 'Access to training, conferences, and professional development opportunities.'
  },
  {
    icon: Users,
    title: 'Collaborative Culture',
    description: 'Join a team of passionate experts committed to excellence.'
  },
  {
    icon: Heart,
    title: 'Work-Life Balance',
    description: 'Flexible working arrangements and comprehensive wellness support.'
  }
];

const jobOpenings = [
  {
    id: 'senior-health-policy-analyst',
    title: 'Senior Health Policy Analyst',
    department: 'Policy Innovation',
    location: 'Abuja, Nigeria',
    type: 'Full-time',
    experience: '5+ years',
    description: 'Lead the development of evidence-based health policies using our proprietary analytical methodology. You will work directly with government ministries and international donors to design transformative health strategies.',
    responsibilities: [
      'Conduct qualitative comparative analysis (QCA) on health system performance',
      'Develop policy briefs and strategic recommendations for government stakeholders',
      'Lead stakeholder engagement sessions with ministry officials',
      'Contribute to the HSL Impact Model framework development',
      'Mentor junior analysts and contribute to team capacity building'
    ],
    requirements: [
      'Master\'s degree in Public Health, Health Policy, or related field',
      '5+ years experience in health policy analysis or strategic consulting',
      'Strong knowledge of health systems in Sub-Saharan Africa',
      'Experience with qualitative and quantitative research methods',
      'Excellent written and verbal communication skills'
    ]
  },
  {
    id: 'health-economist',
    title: 'Health Economist',
    department: 'Health Financing',
    location: 'Abuja, Nigeria / Remote',
    type: 'Full-time',
    experience: '4+ years',
    description: 'Drive our health financing advisory services, helping governments and organizations design sustainable financing models that maximize health outcomes per dollar invested.',
    responsibilities: [
      'Conduct economic evaluations of health interventions',
      'Design performance-based financing mechanisms',
      'Analyze health budget allocations and efficiency',
      'Develop financial sustainability models for health programs',
      'Support public-private partnership structuring'
    ],
    requirements: [
      'Master\'s or PhD in Health Economics, Economics, or related field',
      '4+ years experience in health financing or economic analysis',
      'Proficiency in statistical software (Stata, R, or similar)',
      'Experience with cost-effectiveness and cost-benefit analysis',
      'Knowledge of health financing mechanisms in low-resource settings'
    ]
  },
  {
    id: 'digital-health-specialist',
    title: 'Digital Health Specialist',
    department: 'Health Technology & AI',
    location: 'Abuja, Nigeria',
    type: 'Full-time',
    experience: '3+ years',
    description: 'Lead the integration of AI and digital solutions into health systems, ensuring equitable access and adoption across diverse populations.',
    responsibilities: [
      'Design digital health implementation strategies',
      'Evaluate AI solutions for healthcare applications',
      'Develop digital health equity frameworks',
      'Train healthcare workers on digital tool adoption',
      'Monitor and evaluate digital health interventions'
    ],
    requirements: [
      'Degree in Computer Science, Health Informatics, or related field',
      '3+ years experience in digital health implementation',
      'Understanding of AI/ML applications in healthcare',
      'Experience working in low-resource healthcare settings',
      'Strong project management skills'
    ]
  },
  {
    id: 'monitoring-evaluation-specialist',
    title: 'Monitoring & Evaluation Specialist',
    department: 'Technical Pillars',
    location: 'Abuja, Nigeria',
    type: 'Full-time',
    experience: '4+ years',
    description: 'Design and implement rigorous M&E frameworks that go beyond compliance to drive strategic learning and systemic transformation.',
    responsibilities: [
      'Design theory-based evaluation frameworks',
      'Implement advanced analytical methodology for complex program evaluations',
      'Develop real-time monitoring dashboards',
      'Lead data collection and analysis activities',
      'Produce evaluation reports for donors and stakeholders'
    ],
    requirements: [
      'Master\'s degree in Public Health, Statistics, or related field',
      '4+ years experience in M&E for health programs',
      'Expertise in mixed-methods research approaches',
      'Experience with QCA or other configurational methods preferred',
      'Strong data visualization and reporting skills'
    ]
  },
  {
    id: 'executive-leadership-coach',
    title: 'Executive Leadership Coach',
    department: 'Leadership Development',
    location: 'Abuja, Nigeria / Regional Travel',
    type: 'Full-time',
    experience: '8+ years',
    description: 'Develop and deliver our flagship Ambidextrous Leader Program, coaching health sector executives to become systemic architects of change.',
    responsibilities: [
      'Design and facilitate executive leadership programs',
      'Provide one-on-one coaching to senior health officials',
      'Develop leadership assessment tools and frameworks',
      'Lead change management initiatives',
      'Build partnerships with academic institutions'
    ],
    requirements: [
      'Advanced degree in Organizational Development, Leadership, or related field',
      '8+ years experience in executive coaching or leadership development',
      'Certified coach credentials (ICF or equivalent) preferred',
      'Experience working with government or public sector leaders',
      'Excellent facilitation and presentation skills'
    ]
  },
  {
    id: 'research-associate',
    title: 'Research Associate',
    department: 'Research & Evaluation',
    location: 'Abuja, Nigeria',
    type: 'Full-time',
    experience: '2+ years',
    description: 'Support cutting-edge research on health systems strengthening using innovative analytical approaches.',
    responsibilities: [
      'Conduct literature reviews and evidence synthesis',
      'Support data collection and qualitative research',
      'Assist with data preparation and analysis',
      'Contribute to research publications and reports',
      'Maintain research databases and documentation'
    ],
    requirements: [
      'Master\'s degree in Public Health, Social Sciences, or related field',
      '2+ years research experience in health or development',
      'Strong qualitative research skills',
      'Proficiency in data management and analysis tools',
      'Excellent academic writing abilities'
    ]
  }
];

const experienceOptions = [
  '0-1 years',
  '2-3 years',
  '4-5 years',
  '6-8 years',
  '9-12 years',
  '13+ years'
];

const howHeardOptions = [
  'LinkedIn',
  'HSL Website',
  'Job Board',
  'Referral',
  'Conference/Event',
  'Other'
];

const JobCard = ({ job, onApply }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white border border-slate-200 overflow-hidden hover:border-slate-700 transition-colors"
      data-testid={`job-card-${job.id}`}
    >
      {/* Job Header */}
      <div 
        className="p-6 lg:p-8 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1">
            <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-2 block">
              {job.department}
            </span>
            <h3 className="font-heading text-xl lg:text-2xl text-slate-900 mb-3">{job.title}</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-2 text-slate-500">
                <MapPin size={14} className="text-slate-500" />
                {job.location}
              </span>
              <span className="flex items-center gap-2 text-slate-500">
                <Clock size={14} className="text-slate-500" />
                {job.type}
              </span>
              <span className="flex items-center gap-2 text-slate-500">
                <Briefcase size={14} className="text-slate-500" />
                {job.experience}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button 
              className="flex items-center gap-2 text-teal-600 hover:text-teal-300 transition-colors"
              data-testid={`toggle-job-${job.id}`}
            >
              {isExpanded ? 'Hide Details' : 'View Details'}
              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="border-t border-slate-200"
        >
          <div className="p-6 lg:p-8 space-y-6">
            <div>
              <p className="text-slate-600 leading-relaxed">{job.description}</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h4 className="font-heading text-lg text-slate-900 mb-4">Key Responsibilities</h4>
                <ul className="space-y-2">
                  {job.responsibilities.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-500 text-sm">
                      <div className="w-1.5 h-1.5 bg-teal-400 rounded-full mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-heading text-lg text-slate-900 mb-4">Requirements</h4>
                <ul className="space-y-2">
                  {job.requirements.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-500 text-sm">
                      <div className="w-1.5 h-1.5 bg-slate-500 rounded-full mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="pt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onApply(job);
                }}
                className="btn-primary inline-flex items-center gap-2 group"
                data-testid={`apply-btn-${job.id}`}
              >
                Apply for this Position
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const ApplicationForm = ({ selectedJob, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    position: selectedJob?.title || '',
    linkedin_url: '',
    years_experience: '',
    current_employer: '',
    cover_letter: '',
    resume_url: '',
    how_heard: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedJob) {
      setFormData(prev => ({ ...prev, position: selectedJob.title }));
    }
  }, [selectedJob]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.phone || !formData.position || !formData.years_experience || !formData.cover_letter) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      toast.success('Your application has been submitted successfully!');
      onSuccess();
    } catch (error) {
      console.error('Application form error:', error);
      toast.error('Failed to submit your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white/90 backdrop-blur-sm"
      onClick={onClose}
      data-testid="application-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-slate-50 border border-slate-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-slate-50 border-b border-slate-200 p-6 flex items-center justify-between">
          <div>
            <span className="font-mono text-xs uppercase tracking-widest text-teal-600 block mb-1">
              Apply Now
            </span>
            <h2 className="font-heading text-2xl text-slate-900">
              {selectedJob?.title || 'Job Application'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 transition-colors p-2"
            data-testid="close-application-modal"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="application-form">
          {/* Personal Information */}
          <div>
            <h3 className="font-heading text-lg text-slate-900 mb-4 flex items-center gap-2">
              <User size={18} className="text-teal-600" />
              Personal Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label-mono block mb-2">
                  Full Name <span className="text-teal-600">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                  className="input-hsl"
                  required
                  data-testid="app-input-name"
                />
              </div>
              <div>
                <label className="label-mono block mb-2">
                  Email Address <span className="text-teal-600">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@email.com"
                  className="input-hsl"
                  required
                  data-testid="app-input-email"
                />
              </div>
              <div>
                <label className="label-mono block mb-2">
                  Phone Number <span className="text-teal-600">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+234 XXX XXX XXXX"
                  className="input-hsl"
                  required
                  data-testid="app-input-phone"
                />
              </div>
              <div>
                <label className="label-mono block mb-2">
                  LinkedIn Profile
                </label>
                <input
                  type="url"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleInputChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                  className="input-hsl"
                  data-testid="app-input-linkedin"
                />
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div>
            <h3 className="font-heading text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Briefcase size={18} className="text-teal-600" />
              Professional Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label-mono block mb-2">
                  Position Applying For <span className="text-teal-600">*</span>
                </label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  className="input-hsl"
                  required
                  data-testid="app-input-position"
                >
                  <option value="">Select a position</option>
                  {jobOpenings.map(job => (
                    <option key={job.id} value={job.title}>{job.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-mono block mb-2">
                  Years of Experience <span className="text-teal-600">*</span>
                </label>
                <select
                  name="years_experience"
                  value={formData.years_experience}
                  onChange={handleInputChange}
                  className="input-hsl"
                  required
                  data-testid="app-input-experience"
                >
                  <option value="">Select experience level</option>
                  {experienceOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-mono block mb-2">
                  Current Employer
                </label>
                <input
                  type="text"
                  name="current_employer"
                  value={formData.current_employer}
                  onChange={handleInputChange}
                  placeholder="Organization name"
                  className="input-hsl"
                  data-testid="app-input-employer"
                />
              </div>
              <div>
                <label className="label-mono block mb-2">
                  How did you hear about us?
                </label>
                <select
                  name="how_heard"
                  value={formData.how_heard}
                  onChange={handleInputChange}
                  className="input-hsl"
                  data-testid="app-input-howheard"
                >
                  <option value="">Select an option</option>
                  {howHeardOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Resume/CV */}
          <div>
            <h3 className="font-heading text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Upload size={18} className="text-teal-600" />
              Resume / CV
            </h3>
            <div>
              <label className="label-mono block mb-2">
                Resume/CV Link (Google Drive, Dropbox, etc.)
              </label>
              <input
                type="url"
                name="resume_url"
                value={formData.resume_url}
                onChange={handleInputChange}
                placeholder="https://drive.google.com/file/d/..."
                className="input-hsl"
                data-testid="app-input-resume"
              />
              <p className="text-slate-500 text-xs mt-2">
                Please share a link to your CV/Resume. Make sure the link is publicly accessible.
              </p>
            </div>
          </div>

          {/* Cover Letter */}
          <div>
            <label className="label-mono block mb-2">
              Cover Letter <span className="text-teal-600">*</span>
            </label>
            <textarea
              name="cover_letter"
              value={formData.cover_letter}
              onChange={handleInputChange}
              placeholder="Tell us why you're interested in this position and what makes you a great fit..."
              rows={6}
              className="input-hsl resize-none py-3"
              required
              data-testid="app-input-cover"
            />
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
              data-testid="app-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="app-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const ApplicationSuccess = ({ onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white/90 backdrop-blur-sm"
      onClick={onClose}
      data-testid="success-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-slate-50 border border-slate-200 p-12 max-w-md text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-20 h-20 bg-teal-500/20 border border-teal-500/50 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={40} className="text-teal-600" />
        </div>
        <h3 className="font-heading text-2xl text-slate-900 mb-3">Application Submitted!</h3>
        <p className="text-slate-500 mb-6">
          Thank you for your interest in joining HSL. Our team will review your 
          application and contact you within 2 weeks if your profile matches our requirements.
        </p>
        <button
          onClick={onClose}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="success-close-btn"
        >
          Close
        </button>
      </motion.div>
    </motion.div>
  );
};

export const CareersPage = () => {
  const [searchParams] = useSearchParams();
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    const positionParam = searchParams.get('position');
    if (positionParam) {
      const job = jobOpenings.find(j => j.title === positionParam);
      if (job) {
        setSelectedJob(job);
        setShowApplicationForm(true);
      }
    }
  }, [searchParams]);

  const handleApply = (job) => {
    setSelectedJob(job);
    setShowApplicationForm(true);
  };

  const handleApplicationSuccess = () => {
    setShowApplicationForm(false);
    setShowSuccess(true);
  };

  return (
    <div data-testid="careers-page">
      {/* Hero Section */}
      <section className="section-padding pt-32" data-testid="careers-hero">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.span 
              variants={itemVariants}
              className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-6 block"
            >
              Join Our Team
            </motion.span>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
            >
              Shape the Future of Global Health
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-600 leading-relaxed"
            >
              At HSL, we're building a team of exceptional individuals who are passionate 
              about transforming health systems. Join us in our mission to transition global health 
              from <span className="text-slate-500">default</span> to <span className="text-teal-600">design</span>.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Why Join HSL */}
      <section className="section-padding bg-slate-50" data-testid="why-join">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Why HSL
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
              Build Your Career While Building Better Health Systems
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-slate-200 p-6 hover:border-teal-200 transition-colors group"
                data-testid={`benefit-${index}`}
              >
                <div className="w-12 h-12 bg-teal-100 border border-teal-200 flex items-center justify-center mb-4 group-hover:bg-teal-500/20 transition-colors">
                  <benefit.icon size={24} className="text-teal-600" />
                </div>
                <h3 className="font-heading text-lg text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Image Section */}
      <section className="section-padding" data-testid="team-image">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                A Diverse Team of Global Health Experts
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Our team brings together professionals from across Africa, Asia, and beyond, 
                united by a shared commitment to health systems transformation. We value diverse 
                perspectives and believe that the best solutions come from inclusive collaboration.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <span className="font-heading text-3xl text-teal-600 block">Diverse</span>
                  <span className="text-slate-500 text-sm">Team</span>
                </div>
                <div className="text-center">
                  <span className="font-heading text-3xl text-teal-600 block">Global</span>
                  <span className="text-slate-500 text-sm">Perspective</span>
                </div>
                <div className="text-center">
                  <span className="font-heading text-3xl text-teal-600 block">Expert</span>
                  <span className="text-slate-500 text-sm">Leadership</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1573167101669-476636b96cea?w=800&q=80"
                  alt="Diverse African and Asian Team Collaboration"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-hsl-navy/50 via-transparent to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="section-padding bg-slate-50" data-testid="open-positions">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mb-12"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-4 block">
              Open Positions
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Current Opportunities
            </h2>
            <p className="text-slate-500">
              We're looking for talented individuals to join our growing team. 
              Explore our current openings below and apply online.
            </p>
          </motion.div>

          <div className="space-y-4">
            {jobOpenings.map((job) => (
              <JobCard key={job.id} job={job} onApply={handleApply} />
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="section-padding" data-testid="application-process">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              How to Apply
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
              Our Hiring Process
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Apply Online', description: 'Complete our online application form with your details and cover letter.' },
              { step: '02', title: 'Review', description: 'Our team reviews applications and shortlists candidates within 2 weeks.' },
              { step: '03', title: 'Interview', description: 'Selected candidates participate in virtual interviews with our team.' },
              { step: '04', title: 'Offer', description: 'Successful candidates receive an offer to join the HSL family.' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`process-step-${index}`}
              >
                <div className="w-16 h-16 bg-slate-100 border border-slate-700 flex items-center justify-center mx-auto mb-4">
                  <span className="font-heading text-2xl text-teal-600">{item.step}</span>
                </div>
                <h3 className="font-heading text-lg text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-50" data-testid="careers-cta">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden bg-white border border-slate-200 p-12 md:p-16 text-center"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-transparent to-teal-500/5" />
            
            <div className="relative">
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                Don't See the Right Role?
              </h2>
              <p className="text-slate-500 text-lg mb-8 max-w-2xl mx-auto">
                We're always looking for exceptional talent. Submit a general application 
                and tell us how you can contribute to our mission.
              </p>
              <button
                onClick={() => {
                  setSelectedJob(null);
                  setShowApplicationForm(true);
                }}
                className="btn-primary inline-flex items-center gap-2 group"
                data-testid="general-apply-btn"
              >
                Submit General Application
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Application Modal */}
      {showApplicationForm && (
        <ApplicationForm
          selectedJob={selectedJob}
          onClose={() => setShowApplicationForm(false)}
          onSuccess={handleApplicationSuccess}
        />
      )}

      {/* Success Modal */}
      {showSuccess && (
        <ApplicationSuccess onClose={() => setShowSuccess(false)} />
      )}
    </div>
  );
};
